/**
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceParams:
 *      type: object
 *      properties:
 *        RegattaResultTableId:
 *          description: ID of the associated RegattaResultTable
 *          type: string
 *        Public:
 *          description: Public of the RegattaResultTableRace
 *          type: boolean
 *        Name:
 *          description: Name of the RegattaResultTableRace
 *          type: string
 *        Type:
 *          description: Type of the RegattaResultTableRace
 *          $ref: '#/components/enums/RegattaResultTableRaceType'
 *        Approved:
 *          description: Approved of the RegattaResultTableRace
 *          type: boolean
 *        Undiscard:
 *          description: Undiscard of the RegattaResultTableRace
 *          type: boolean
 *        Dominant:
 *          description: Dominant of the RegattaResultTableRace
 *          type: boolean
 *        Distance:
 *          description: Distance of the RegattaResultTableRace
 *          type: integer
 *        Coef:
 *          description: Coef of the RegattaResultTableRace
 *          type: integer
 *        Year:
 *          description: Year of the RegattaResultTableRace
 *          type: integer
 *        Month:
 *          description: Month of the RegattaResultTableRace
 *          type: integer
 *        Day:
 *          description: Day of the RegattaResultTableRace
 *          type: integer
 *        Hour:
 *          description: Hour of the RegattaResultTableRace
 *          type: integer
 *        Minute:
 *          description: Minute of the RegattaResultTableRace
 *          type: integer
 *        Second:
 *          description: Second of the RegattaResultTableRace
 *          type: integer
 *        Course:
 *          description: Course of the RegattaResultTableRace
 *          type: string
 *        Tws:
 *          description: Tws of the RegattaResultTableRace
 *          type: string
 *        Twa:
 *          description: Twa of the RegattaResultTableRace
 *          type: string
 *        ShowTimeColumn:
 *          description: ShowTimeColumn of the RegattaResultTableRace
 *          type: boolean
 *        ShowPointsColumn:
 *          description: ShowPointsColumn of the RegattaResultTableRace
 *          type: boolean
 *        ShowPlaceColumn:
 *          description: ShowPlaceColumn of the RegattaResultTableRace
 *          type: boolean
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableRaceParams'
 *        - type: object
 *          required:
 *            - RegattaResultTableId
 *            - Public
 *            - Name
 *            - Type
 *            - Approved
 *            - Undiscard
 *            - Dominant
 *            - Coef
 *            - Year
 *            - Month
 *            - Day
 *            - Hour
 *            - Minute
 *            - Second
 *            - ShowTimeColumn
 *            - ShowPointsColumn
 *            - ShowPlaceColumn
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaResultTableRace
 *              type: string
 *        - $ref: '#/definitions/RegattaResultTableRaceParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableRaceUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableRaceQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaResultTableRace:
 *      allOf:
 *        - $ref: '#/definitions/RegattaResultTableRaceCreateParams'
 *        - $ref: '#/definitions/RegattaResultTableRaceUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaResultTableRace:
 *        allOf:
 *          - $ref: '#/definitions/RegattaResultTableRace'
 *          - type: object
 *            properties:
 *              RegattaResultTable:
 *                description: RegattaResultTable of the RegattaResultTableRace
 *                $ref: '#/components/schemas/RegattaResultTable'
 *              RegattaResultTableRaceResults:
 *                description: RegattaResultTableRaceResults of the RegattaResultTableRace
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaResultTableRaceResult'
 */

// Enums
import {
  RegattaResultTableRaceType,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaResultTableId: string;
};

export type $Params = {
  Approved: boolean;
  Coef: number;
  Course: null | string;
  Day: number;
  Distance: null | number;
  Dominant: boolean;
  Hour: number;
  Minute: number;
  Month: number;
  Name: string;
  Public: boolean;
  Second: number;
  ShowPlaceColumn: boolean;
  ShowPointsColumn: boolean;
  ShowTimeColumn: boolean;
  Twa: null | string;
  Tws: null | string;
  Type: RegattaResultTableRaceType;
  Undiscard: boolean;
  Year: number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaResultTable?: $Model<'RegattaResultTable'>;
  RegattaResultTableRaceResults?: Array<$Model<'RegattaResultTableRaceResult'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Approved: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Coef: {
    allowNull: false,
    type: Sequelize.DOUBLE(
      2,
      1,
    ),
  },
  Course: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Day: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Distance: {
    allowNull: true,
    type: Sequelize.DOUBLE(
      8,
      2,
    ),
  },
  Dominant: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Hour: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Minute: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Month: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Second: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
  ShowPlaceColumn: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  ShowPointsColumn: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  ShowTimeColumn: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Twa: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Tws: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Type: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaResultTableRaceType)),
    validate: {
      notEmpty: true,
    },
  },
  Undiscard: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Year: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaResultTableRace'> => {
  const RegattaResultTableRace = stack.define(
    'RegattaResultTableRace',
    fields(Sequelize),
    {
      tableName: 'RegattaResultTableRaces',
    },
  );

  RegattaResultTableRace.associate = (models: $ModelClasses) => {
    models.RegattaResultTableRace.belongsTo(
      models.RegattaResultTable,
      {
        as: 'RegattaResultTable',
        foreignKey: 'RegattaResultTableId',
      },
    );
    models.RegattaResultTableRace.hasMany(
      models.RegattaResultTableRaceResult,
      {
        as: 'RegattaResultTableRaceResults',
        foreignKey: 'RegattaResultTableRaceId',
      },
    );
  };

  RegattaResultTableRace.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaResultTableRace;
});
